import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import Api from "../../../services/Api";
import { queryKeys } from "../../../lib/query";
import { roundToDecimal, formatSecondsToDuration, isEnphaseUser } from "../../../lib/utils/utility";
import { SITE_UTILIZATION_URL, DATE_FORMAT_DD_MM_YYYY, DATE_FORMAT_DISPLAY } from "../constants";

dayjs.extend(customParseFormat);

/**
 * Extract unique, non-empty siteIds from fleet charger data.
 * @param {Array} fleetData - Array of charger objects
 * @returns {string[]} Unique siteId strings
 */
const extractUniqueSiteIds = (fleetData = []) => [...new Set(fleetData.map(c => c.siteId).filter(Boolean))];

/**
 * Safely coerce a value to a number, returning 0 for nullish/NaN.
 * @param {*} value - Raw value from API response
 * @returns {number}
 */
const toSafeNumber = value => (value !== null && value !== undefined ? Number(value) || 0 : 0);

/**
 * Aggregate utilization results from multiple site API responses.
 * @param {Array} results - Array of Promise.allSettled results
 * @returns {{ totals: object, dailyMap: object, validSiteCount: number }}
 */
const aggregateUtilizationResults = (results = []) => {
  const totals = {
    totalSessions: 0,
    totalSessionsCost: 0,
    totalEnergyConsumedKwh: 0,
    totalUtilizationTimeSeconds: 0,
    utilizationPercentSum: 0,
  };
  let validSiteCount = 0;
  const dailyMap = {};

  results
    .filter(r => r.status === "fulfilled" && r.value)
    .map(r => r.value)
    .forEach(res => {
      totals.totalSessions += toSafeNumber(res.totalSessions);
      totals.totalSessionsCost += toSafeNumber(res.totalSessionsCost);
      totals.totalEnergyConsumedKwh += toSafeNumber(res.totalEnergyConsumedKwh);
      totals.totalUtilizationTimeSeconds += toSafeNumber(res.totalUtilizationTimeSeconds);

      if (res.utilizationPercent !== null && res.utilizationPercent !== undefined) {
        totals.utilizationPercentSum += toSafeNumber(res.utilizationPercent);
        validSiteCount += 1;
      }

      if (!Array.isArray(res.dailyData)) return;

      res.dailyData.forEach(d => {
        if (!dailyMap[d.date]) {
          dailyMap[d.date] = { energy: 0, sessions: 0 };
        }
        dailyMap[d.date].energy += toSafeNumber(d.totalEnergyConsumedKwh);
        dailyMap[d.date].sessions += toSafeNumber(d.totalSessions);
      });
    });

  return { totals, dailyMap, validSiteCount };
};

/**
 * Build a contiguous daily time series between two dates, filling gaps with zeros.
 * @param {string} startDate - Start date in DD-MM-YYYY format
 * @param {string} endDate - End date in DD-MM-YYYY format
 * @param {object} dailyMap - Aggregated daily map keyed by DD-MM-YYYY
 * @returns {Array<{ date: string, energy: number, sessions: number }>}
 */
const buildDailyTimeSeries = (startDate, endDate, dailyMap = {}) => {
  const start = dayjs(startDate, DATE_FORMAT_DD_MM_YYYY);
  const end = dayjs(endDate, DATE_FORMAT_DD_MM_YYYY);
  const dayCount = end.diff(start, "day") + 1;

  return Array.from({ length: dayCount }, (_, i) => {
    const current = start.add(i, "day");
    const key = current.format(DATE_FORMAT_DD_MM_YYYY);
    const { energy = 0, sessions = 0 } = dailyMap[key] ?? {};

    return {
      date: current.format(DATE_FORMAT_DISPLAY),
      energy: roundToDecimal(energy),
      sessions,
    };
  });
};

/**
 * Custom hook that:
 * 1. Extracts unique siteIds from fleet charger data
 * 2. Calls POST /sessions/v1/site/{siteId}/utilization for each site
 * 3. Aggregates the results across all sites
 *
 * @param {Array} fleetData - Array of charger objects from ev_charger API
 * @param {string} startDate - Start date in DD-MM-YYYY format
 * @param {string} endDate - End date in DD-MM-YYYY format
 * @returns {{ data: object|null, isLoading: boolean, isError: boolean, refresh: Function }}
 */
export function useFleetUtilization(fleetData, startDate, endDate) {
  const queryClient = useQueryClient();

  const hasData = Boolean(!isEnphaseUser() && fleetData?.length && startDate && endDate);

  const {
    data = null,
    isLoading,
    isError,
  } = useQuery({
    queryKey: queryKeys.utilization.fleet(startDate, endDate),
    queryFn: async () => {
      const siteIds = extractUniqueSiteIds(fleetData);
      if (siteIds.length === 0) return null;

      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Each per-site call is cached under its own query key so that
      // SiteUtilizationKpis on the Station page can reuse the result
      // without making a duplicate network request.
      const results = await Promise.allSettled(
        siteIds.map(siteId =>
          queryClient
            .fetchQuery({
              queryKey: queryKeys.utilization.bySite(siteId, startDate, endDate),
              queryFn: () =>
                Api.post(`${SITE_UTILIZATION_URL}/${siteId}/utilization`, {
                  startDate,
                  endDate,
                  timezone,
                }),
              staleTime: 5 * 60 * 1000,
            })
            .then(res => ({ siteId, data: res }))
        )
      );

      // Build per-site map for consumers (e.g. TopSites)
      const perSite = {};
      results.forEach(r => {
        if (r.status === "fulfilled" && r.value) {
          perSite[r.value.siteId] = r.value.data;
        }
      });

      // Re-wrap for aggregation (aggregateUtilizationResults expects { status, value })
      const aggResults = results.map(r =>
        r.status === "fulfilled" ? { status: "fulfilled", value: r.value?.data } : r
      );

      const { totals, dailyMap, validSiteCount } = aggregateUtilizationResults(aggResults);
      const dailyData = buildDailyTimeSeries(startDate, endDate, dailyMap);

      const avgUtilizationPercent =
        validSiteCount > 0 ? roundToDecimal(totals.utilizationPercentSum / validSiteCount, 1) : 0;

      return {
        totalSessions: totals.totalSessions,
        totalSessionsCost: roundToDecimal(totals.totalSessionsCost),
        totalEnergyConsumedKwh: roundToDecimal(totals.totalEnergyConsumedKwh),
        totalUtilizationTimeSeconds: totals.totalUtilizationTimeSeconds,
        totalUtilizationTimeFormatted: formatSecondsToDuration(totals.totalUtilizationTimeSeconds),
        avgUtilizationPercent,
        siteCount: siteIds.length,
        successfulSites: validSiteCount,
        dailyData,
        perSite,
      };
    },
    enabled: hasData,
    staleTime: 5 * 60 * 1000,
  });

  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: queryKeys.utilization.fleet(startDate, endDate) }),
    [queryClient, startDate, endDate]
  );

  return { data, isLoading, isError, refresh };
}

export default useFleetUtilization;
